import {
    computed,
    inject,
    provide,
    reactive,
    toValue,
    type ComputedRef,
    type InjectionKey,
    type MaybeRefOrGetter,
} from 'vue'

/** Matches the short-url shape produced by the backend: `<host>/api/r/<hash>` */
const SHORT_URL_RE = /\/api\/r\/([A-Za-z0-9_-]{1,20})(?:[?#]|$)/

/** Global variant matching whole short urls embedded in free text (group 1 = hash) */
const FULL_SHORT_URL_RE =
    /https?:\/\/[^\s)"'<>]*\/api\/r\/([A-Za-z0-9_-]{1,20})/g

export type ShortUrlBatchResolver = (
    hashes: string[],
) => Promise<Record<string, string>>

function extractHash(url: string): string | undefined {
    return SHORT_URL_RE.exec(url)?.[1]
}

export interface ResolvedUrl {
    /** The resolved url, or the original short url until it arrives / on failure */
    url: string | undefined
    /** True while the short url is being resolved (use to show a skeleton) */
    loading: boolean
}

export function createShortUrlResolver(batchResolve: ShortUrlBatchResolver) {
    // hash -> resolved long url (reactive so templates update on arrival)
    const cache = reactive(new Map<string, string>())
    // hashes whose batch has settled (resolved or not) — stops the loading state
    const settled = reactive(new Set<string>())
    const pending = new Set<string>()
    let scheduled = false

    async function flush() {
        scheduled = false
        const hashes = [...pending]
        pending.clear()
        if (hashes.length === 0) {
            return
        }
        try {
            const urls = await batchResolve(hashes)
            for (const [hash, url] of Object.entries(urls)) {
                cache.set(hash, url)
            }
        } catch {
            // leave short urls untouched on failure
        } finally {
            // mark every requested hash as settled so it stops loading even
            // when the backend returned no match (falls back to the short url)
            for (const hash of hashes) {
                settled.add(hash)
            }
        }
    }

    function request(hash: string) {
        if (cache.has(hash) || pending.has(hash)) {
            return
        }
        pending.add(hash)
        if (!scheduled) {
            scheduled = true
            // batch every hash requested within the same tick into one call
            queueMicrotask(flush)
        }
    }

    /** Resolves a short url, exposing both the value and its loading state */
    function resolve(
        input: MaybeRefOrGetter<string | undefined>,
    ): ComputedRef<ResolvedUrl> {
        return computed(() => {
            const url = toValue(input)
            const hash = url ? extractHash(url) : undefined
            if (!hash) {
                return { url, loading: false }
            }
            const resolved = cache.get(hash)
            if (resolved !== undefined) {
                return { url: resolved, loading: false }
            }
            request(hash)
            return { url, loading: !settled.has(hash) }
        })
    }

    /**
     * Resolves every short url embedded in a free-text string, replacing each
     * with its real target (full substitution). Unresolved hashes stay as short
     * urls until their batch settles. Meant for exported/copied content (email,
     * message bodies) and prose, where a short url must become the real one.
     */
    function resolveText(
        input: MaybeRefOrGetter<string | undefined>,
    ): ComputedRef<string> {
        return computed(() => {
            const text = toValue(input) ?? ''
            if (!text) {
                return text
            }
            return text.replace(FULL_SHORT_URL_RE, (full, hash: string) => {
                const resolved = cache.get(hash)
                if (resolved !== undefined) {
                    return resolved
                }
                request(hash)
                return full
            })
        })
    }

    return { resolve, resolveText }
}

export type ShortUrlResolver = ReturnType<typeof createShortUrlResolver>

const ShortUrlResolverKey: InjectionKey<ShortUrlResolver> =
    Symbol('shortUrlResolver')

/** Call once in the chat root, passing the api client's batch resolver */
export function provideShortUrlResolver(
    batchResolve: ShortUrlBatchResolver,
): ShortUrlResolver {
    const resolver = createShortUrlResolver(batchResolve)
    provide(ShortUrlResolverKey, resolver)
    return resolver
}

/** No-op fallback when no resolver is provided: returns urls untouched */
const identityResolver: ShortUrlResolver = {
    resolve: (input) =>
        computed(() => ({ url: toValue(input), loading: false })),
    resolveText: (input) => computed(() => toValue(input) ?? ''),
}

export function useShortUrlResolver(): ShortUrlResolver {
    return inject(ShortUrlResolverKey, identityResolver)
}
